import React, { type FC, useEffect } from "react";
import withMemberData from "../../hoc/withMemberData";
import { FormGroup, Label, Input } from "reactstrap";
import AddressForm from "../molecules/AddressForm";
import PageHeader from "../molecules/PageHeader";
import trackService, { AUDITEVENTTYPE } from "services/trackService";
import EmergencyContactForm from "components/molecules/EmergencyContactForm";
import ContactInformationForm from "components/molecules/ContactInformationForm";
import { Member } from "types/Member";
import MailingAddressForm from "components/molecules/MailingAddressForm";
import { Grid, Stack } from "@mui/material";

type AccountInformationProps = {
  member: Member;
  memberLoadedOnce: boolean;
  reloadMember: () => void;
  loadingMember: boolean;
};

const AccountInformation: FC<AccountInformationProps> = (props) => {
  useEffect(() => {
    const trackPageVisitSub = trackService
      .customEvent$({
        eventType: AUDITEVENTTYPE.MemberPortalViewAccountInformation,
      })
      .subscribe();

    return () => {
      trackPageVisitSub.unsubscribe();
    };
  }, []);

  return (
    <div className="pb-5">
      <PageHeader>My Information</PageHeader>
      <p className="mb-5">
        Your account information is provided for your convenience. If you need
        to make any changes, please edit the individual sections below and click
        "Save Changes" when you are finished. If you have any questions, please
        call your ACCESS On-Demand Concierge at 1-833-242-2223 (TTY 711), 24
        hours a day, seven days a week.
      </p>
      <Stack spacing={8} direction="column">
        <Grid
          spacing={2}
          container
          columns={{
            xs: 1,
            sm: 3,
            md: 5,
          }}
        >
          <Grid size={1}>
            <FormGroup>
              <Label id="label-for-memberId" for="memberId">
                My Member ID #
              </Label>
              <Input
                aria-labelledby="label-for-memberId"
                readOnly
                name="memberId"
                id="memberId"
                defaultValue={props.member && props.member.member_number}
              />
            </FormGroup>
          </Grid>
          <Grid size={1}>
            <FormGroup>
              <Label id="label-for-firstName" for="firstName">
                First Name
              </Label>
              <Input
                aria-labelledby="label-for-firstName"
                readOnly
                name="firstName"
                id="firstName"
                defaultValue={props.member && props.member.first_name}
              />
            </FormGroup>
          </Grid>
          <Grid size={1}>
            <FormGroup>
              <Label id="label-for-lastName" for="lastName">
                Last Name
              </Label>
              <Input
                aria-labelledby="label-for-lastName"
                readOnly
                name="lastName"
                id="lastName"
                defaultValue={props.member && props.member.last_name}
              />
            </FormGroup>
          </Grid>
          <Grid size={1}>
            <FormGroup>
              <Label id="label-for-registeredEmail" for="registeredEmail">
                Registered Email
              </Label>
              <Input
                aria-labelledby="label-for-registered-email"
                readOnly
                name="registeredEmail"
                id="registeredEmail"
                defaultValue={props.member && props.member.email}
              />
            </FormGroup>
          </Grid>
        </Grid>
        {props.member &&
        (props.member.addresses || []).filter(
          (address) => address.address_type === "Home"
        ).length ? (
          <AddressForm
            {...props.member.addresses.filter(
              (address) => address.address_type === "Home"
            )[0]}
          />
        ) : null}
        {props.member &&
        (props.member.addresses || []).filter(
          (address) => address.address_type === "Mailing"
        ).length ? (
          <MailingAddressForm
            initialLoading={!props.memberLoadedOnce && props.loadingMember}
            memberId={props.member.member_number}
            onUpdateSuccess={props.reloadMember}
            {...props.member.addresses
              .filter(
                (address) =>
                  address.address_type?.trim().toLowerCase() === "mailing"
              )
              ?.map((address) => ({
                addressLine1: address.street_name_1,
                addressLine2: address.street_name_2,
                city: address.city_name,
                state: address.state,
                zip: address.zip_code,
              }))?.[0]}
          />
        ) : null}

        <ContactInformationForm
          homePhone={props.member?.home_phone}
          cellPhone={props.member?.cell_phone}
          email={props.member?.email}
          memberId={props.member?.member_number}
          readOnly={props.member?.termed}
          initialLoading={!props.memberLoadedOnce && props.loadingMember}
          onUpdateSuccess={props.reloadMember}
        />
        <EmergencyContactForm
          memberId={props.member?.member_number}
          readOnly={props.member?.termed}
          onUpdateSuccess={props.reloadMember}
          initialLoading={!props.memberLoadedOnce && props.loadingMember}
          emergencyContact={{
            emergencyContactFullName: props.member?.emergencyContactName,
            emergencyContactPhoneNumber: props.member?.emergencyContactPhone,
            emergencyContactEmail: props.member?.emergencyContactEmail,
            emergencyContactRelation: props.member?.emergencyContactRelation,
          }}
        />
      </Stack>
    </div>
  );
};

export default withMemberData(AccountInformation);

export { AccountInformation };
